import React, { useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField } from '@mui/material';
import { Block as CancelIcon } from '@mui/icons-material';

/**
 * Withdraws a rate agreement negotiation that has not been fully signed
 * (POST /api/rate-agreements/:id/cancel). The server decides who may: White
 * Knight management or the provider party to the agreement.
 */
export default function CancelNegotiationButton({ agreementId, onCanceled, disabled, size = 'medium', label = 'Cancel Negotiation' }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/rate-agreements/${agreementId}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ reason })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || `Could not cancel the negotiation (${res.status})`);
      setOpen(false);
      setReason('');
      if (onCanceled) onCanceled(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        color="error"
        size={size}
        startIcon={<CancelIcon />}
        onClick={() => setOpen(true)}
        disabled={disabled || !agreementId}
      >
        {label}
      </Button>
      <Dialog open={open} onClose={() => !busy && setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Cancel this negotiation?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The agreement can no longer be signed and the other party is emailed that the negotiation was withdrawn.
            The rates currently on file stay in effect, and a new agreement can be generated at any time.
          </DialogContentText>
          <TextField
            label="Reason (optional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            fullWidth
            multiline
            rows={2}
            margin="normal"
            inputProps={{ maxLength: 300 }}
          />
          {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={busy}>Keep negotiating</Button>
          <Button onClick={confirm} color="error" variant="contained" disabled={busy}>
            {busy ? 'Canceling…' : 'Cancel negotiation'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
